import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { AthleteTabMenu } from "./AthleteTabMenu";
import AthleteProfileCard from "../component/AthleteProfileCard";
import API_URL from "../../api/API";

export const AdminAthleteSpeedPerformanceCharts = () => {
  const navigate = useNavigate();
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();

  /* ---------------- Athlete ---------------- */
  const [athlete, setAthlete] = useState(null);
  const [loadingAthlete, setLoadingAthlete] = useState(false);

  /* ---------------- Filters ---------------- */
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [event, setEvent] = useState("100");
  const [intensity, setIntensity] = useState("98");

  /* ---------------- Chart Data ---------------- */
  const [distanceHistory, setDistanceHistory] = useState([]);
  const [selectedDistance, setSelectedDistance] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);

  const [chartKey, setChartKey] = useState(0);

  /* ---------------- Fetch Athlete ---------------- */
  useEffect(() => {
    const fetchAthlete = async () => {
      setLoadingAthlete(true);
      try {
        const res = await fetch(`${API_URL.GET_ATHLETE_BY_ID}/${athlete_id}`, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        const json = await res.json();
        if (res.ok) setAthlete(json);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingAthlete(false);
      }
    };

    fetchAthlete();
  }, [athlete_id, staff.token]);

  /* ---------------- Apply Filters ---------------- */
  const handleApplyFilter = async () => {
    if (!startDate || !endDate || !event) {
      alert("Please select Start Date, End Date and Event");
      return;
    }

    setLoadingCharts(true);

    try {
      /* -------- Graph 2: Distance History -------- */
      const distRes = await fetch(
        `${API_URL.ADMIN_COACHED_ATHLETE_GET_DISTANCE_DATA}` +
          `?athlete_id=${athlete_id}` +
          `&start_date=${startDate}` +
          `&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${staff.token}` } },
      );

      const distJson = await distRes.json();
      if (distRes.ok) {
        setDistanceHistory(distJson.data || []);

        const distances = Array.from(
          new Set((distJson.data || []).map((d) => Number(d.distance))),
        ).sort((a, b) => a - b);

        setSelectedDistance(distances[0] || null);
      }

      setChartKey((prev) => prev + 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCharts(false);
    }
  };

  /* ---------------- Normalize Distance History ---------------- */
  const normalizedDistanceHistory = useMemo(() => {
    return distanceHistory
      .map((d) => ({
        date: d.date,
        distance: Number(d.distance),
        actual_time: Number(d.actual_time),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [distanceHistory]);

  /* ---------------- Available Distances (DATA DRIVEN) ---------------- */
  const availableDistances = useMemo(() => {
    return Array.from(
      new Set(normalizedDistanceHistory.map((d) => d.distance)),
    ).sort((a, b) => a - b);
  }, [normalizedDistanceHistory]);

  const tableData = useMemo(() => {
    const filtered = normalizedDistanceHistory
      .filter((d) => d.distance === Number(selectedDistance))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return filtered.map((row, index) => {
      const prev = filtered[index - 1];

      const change =
        prev && row.actual_time != null && prev.actual_time != null
          ? row.actual_time - prev.actual_time
          : null;

      return {
        ...row,
        change,
      };
    });
  }, [normalizedDistanceHistory, selectedDistance]);

  const stats = useMemo(() => {
    if (!tableData.length) return null;

    const times = tableData.map((d) => d.actual_time).filter((t) => t != null);

    if (!times.length) return null;

    const first = times[0];
    const last = times[times.length - 1];

    const improvement = first && last ? ((first - last) / first) * 100 : 0;

    const best = Math.min(...times);

    const avg = times.reduce((sum, t) => sum + t, 0) / times.length;

    return {
      improvement,
      best,
      avg,
    };
  }, [tableData]);

  /* ---------------- UI ---------------- */
  return (
    <div className="md:flex md:h-screen">
      <StaffSideMenu />

      <div className="md:flex-1 bg-bgBase overflow-y-auto max-md:pt-4 pb-5">
        {/* Header */}
        <div className="bg-white shadow md:sticky md:top-0 z-30 -mb-4">
          {loadingAthlete ? (
            <div className="px-10 py-2 font-inter font-normal">
              <p className="text-sm text-gray-500">Loading athlete…</p>
            </div>
          ) : athlete ? (
            <AthleteProfileCard athlete={athlete} />
          ) : null}
        <AthleteTabMenu active="speed-charts" />
        </div>


        <div className="pt-10 px-5 md:px-8 xl:px-16">
          <div className="bg-white border border-tblBorder p-5 md:p-6 rounded-14 w-full max-w-[1336px]">
            <h2 className="text-dark font-inter text-xl font-medium mb-6">
              Speed Performance Charts
            </h2>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              <div className="max-xl:order-2 xl:col-span-8">
                  {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {/* Improvement */}
                      <div className="bg-white border border-tblBorder rounded-14 p-4">
                        <p className="font-inter font-medium text-xs text-slate100 mb-1">
                          Improvement
                        </p>
                        <p
                          className={`font-inter text-lg font-semibold ${
                            stats.improvement >= 0
                              ? "text-green-600"
                              : "text-red-500"
                          }`}
                        >
                          {stats.improvement.toFixed(2)}%
                        </p>
                      </div>

                      {/* Best Time */}
                      <div className="bg-white border border-tblBorder rounded-14 p-4">
                        <p className="font-inter font-medium text-xs text-slate100 mb-1">
                          Best Time
                        </p>
                        <p className="font-inter text-lg font-semibold text-dark">
                          {stats.best.toFixed(2)}s
                        </p>
                      </div>

                      {/* Average Time */}
                      <div className="bg-white border border-tblBorder rounded-14 p-4">
                        <p className="font-inter font-medium text-xs text-slate100 mb-1">
                          Average Time
                        </p>
                        <p className="font-inter text-lg font-semibold text-dark">
                          {stats.avg.toFixed(2)}s
                        </p>
                      </div>
                    </div>
                  )}

                  {/* GRAPH 2 */}
                  {selectedDistance && (
                    <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6">
                      <h3 className="font-inter text-base font-semibold mb-3">
                        {selectedDistance}m – Performance Over Time
                      </h3>

                      <div className="flex gap-2 mb-4 flex-wrap">
                        {availableDistances.map((distance) => (
                          <button
                            key={distance}
                            onClick={() => setSelectedDistance(distance)}
                            className={`px-3 py-1 font-inter font-normal text-xs md:text-sm rounded border ${
                              selectedDistance === distance
                                ? "bg-black text-white"
                                : "bg-white"
                            }`}
                          >
                            {distance}m
                          </button>
                        ))}
                      </div>

                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          key={`dist-${chartKey}-${selectedDistance}`}
                          data={normalizedDistanceHistory.filter(
                            (d) => d.distance === Number(selectedDistance),
                          )}
                        >
                          <CartesianGrid
                            stroke="#e5e7eb"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            dataKey="date"
                            tick={{
                              fill: "#6b7280",
                              fontSize: 12,
                              fontFamily: "Inter",
                              fontWeight: 400,
                            }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{
                              fill: "#6b7280",
                              fontSize: 12,
                              fontFamily: "Inter",
                              fontWeight: 400,
                            }}
                            axisLine={false}
                            tickLine={false}
                          />
                          {/* Tooltip clean */}
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              fontFamily: "Inter",
                              fontWeight: 400,
                              fontSize: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="actual_time"
                            name="Actual Time"
                            stroke="#111827" // soft black (not pure #000)
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#111827" }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {selectedDistance && (
                    <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6 mt-6">
                      <p className="font-inter font-medium text-sm mb-3">
                        Performance History
                      </p>

                      <div className="overflow-x-auto">
                        <table className="table-auto w-full">
                          <thead className="sticky top-0 border-b border-tblBorder">
                            <tr className="text-slate100 font-inter !font-medium text-xs md:text-sm uppercase">
                              <th className="py-2 text-left">Date</th>
                              <th className="py-2 text-left">Distance (m)</th>
                              <th className="py-2 text-left">Time</th>
                              <th className="py-2 text-left">
                                Change From Previous
                              </th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-tblBorder">
                            {tableData.length === 0 ? (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="py-4 text-center text-gray-500"
                                >
                                  No data available
                                </td>
                              </tr>
                            ) : (
                              tableData.map((r, i) => (
                                <tr key={i} className="text-dark font-inter font-normal text-sm">
                                  <td className="py-2">
                                    {new Date(r.date).toLocaleDateString()}
                                  </td>

                                  <td className="py-2">{r.distance}</td>

                                  <td className="py-2 font-medium">
                                    {r.actual_time?.toFixed(2)}
                                  </td>

                                  <td
                                    className={`py-2 ${
                                      r.change != null
                                        ? r.change > 0
                                          ? "text-red-500"
                                          : "text-green-600"
                                        : ""
                                    }`}
                                  >
                                    {r.change != null
                                      ? r.change.toFixed(2)
                                      : "-"}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {loadingCharts && (
                    <div className="bg-bgBas p-3 rounded-10">
                      <p className="text-center text-sm text-gray-500 mt-4">
                        Loading charts…
                      </p>
                    </div>
                  )}
              </div>
              <div className="max-xl:order-1 xl:col-span-4">
                {/* Filters */}
                <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6">
                  <p className="font-inter font-medium text-dark text-sm capitalize mb-4">
                    Filters
                  </p>
                  <div className="flex flex-col gap-3 admindate mb-4">
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        start date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        last date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        event
                      </label>
                      <select
                        value={event}
                        onChange={(e) => setEvent(e.target.value)}
                        className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="100">100m</option>
                        <option value="200">200m</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    {/* <div className="flex flex-col gap-2 mb-4">
                      {["98", "95"].map((v) => (
                        <label key={v} className="flex items-center gap-2">
                          <input
                            type="radio"
                            value={v}
                            checked={intensity === v}
                            onChange={(e) => setIntensity(e.target.value)}
                            className="accent-black"
                          />
                          <span className="text-dark font-inter font-normal text-sm">
                            {v}%
                          </span>
                        </label>
                      ))}
                    </div> */}

                    <div className="text-right">
                      <button
                        onClick={handleApplyFilter}
                        className="bg-dark w-full text-center text-white px-6 py-2 rounded-10 font-inter font-medium text-sm hover:bg-black"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAthleteSpeedPerformanceCharts;
